import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const EMPTY = { nome: '', tipo: 'externa', contato: '', obs: '' };

export default function Suppliers() {
  const { user } = useAuth();
  const isManager = ['admin', 'supervisor'].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => { api.get('/suppliers').then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...EMPTY, ...s }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.nome) { toast.error('Nome é obrigatório'); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/suppliers/${editing.supplier_id}`, form); toast.success('Fornecedor atualizado'); }
      else { await api.post('/suppliers', form); toast.success('Fornecedor cadastrado'); }
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };
  const doDelete = async () => {
    try { await api.delete(`/suppliers/${delTarget.supplier_id}`); toast.success('Fornecedor removido'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao remover'); }
    finally { setDelTarget(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">Fornecedores</h2>
          <p className="text-sm text-muted-foreground">Parceiros (JMC, SOAR, FUJJITurismo) e viagens próprias.</p>
        </div>
        {isManager && <Button onClick={openNew} data-testid="suppliers-new-button"><Plus className="h-4 w-4 mr-2" /> Novo fornecedor</Button>}
      </div>

      <Card className="p-4">
        {loading ? <p className="text-muted-foreground py-8 text-center">Carregando...</p> : items.length === 0 ? (
          <div className="py-12 text-center"><Truck className="h-8 w-8 mx-auto text-muted-foreground" /><p className="mt-3 text-muted-foreground">Nenhum fornecedor.</p></div>
        ) : (
          <Table data-testid="suppliers-table">
            <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Tipo</TableHead><TableHead>Contato</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((s) => (
                <TableRow key={s.supplier_id}>
                  <TableCell className="font-medium">{s.nome}</TableCell>
                  <TableCell><Badge variant="secondary">{s.tipo === 'propria' ? 'Própria' : 'Externa'}</Badge></TableCell>
                  <TableCell className="text-muted-foreground">{s.contato || '-'}</TableCell>
                  <TableCell>
                    {isManager && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(s)}><Pencil className="h-4 w-4 mr-2" /> Editar</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDelTarget(s)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Excluir</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Editar fornecedor' : 'Novo fornecedor'}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Nome *</Label><Input value={form.nome} onChange={set('nome')} className="mt-1.5" data-testid="supplier-nome" /></div>
            <div><Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger className="mt-1.5" data-testid="supplier-tipo"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="externa">Externa (parceiro)</SelectItem><SelectItem value="propria">Própria</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Contato</Label><Input value={form.contato} onChange={set('contato')} className="mt-1.5" /></div>
            <div><Label>Observações</Label><Textarea value={form.obs} onChange={set('obs')} rows={2} className="mt-1.5" /></div>
            <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving} data-testid="supplier-save">{saving ? 'Salvando...' : 'Salvar'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir fornecedor?</AlertDialogTitle><AlertDialogDescription>Deseja excluir {delTarget?.nome}?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
